import { useCallback, useEffect, useRef, useState } from "react";

export interface AutoAdvance {
  /** Seconds left, or `null` when nothing is counting — disabled, cancelled, or already fired. */
  remaining: number | null;
  /** Stops the countdown for good. `onElapsed` will not fire, and nothing restarts it. */
  cancel: () => void;
}

/**
 * Counts down from `seconds` and fires `onElapsed` once, unless the reader cancels first.
 *
 * `seconds: null` disables it entirely, which is the default everywhere: a screen that moves on its own is
 * the exception — a verdict that landed while the reader was watching — not the rule. Cancelling is not a
 * nicety either: a screen that navigates by itself with no way to stop it fails WCAG 2.2.1.
 */
export function useAutoAdvance(seconds: number | null, onElapsed: () => void): AutoAdvance {
  // Held in a ref so a fresh closure on every render does not restart the countdown from the top.
  const onElapsedRef = useRef(onElapsed);
  useEffect(() => {
    onElapsedRef.current = onElapsed;
  }, [onElapsed]);

  const [budget, setBudget] = useState<number | null>(seconds);
  const [remaining, setRemaining] = useState<number | null>(seconds);
  const [cancelled, setCancelled] = useState(false);

  // React's documented "adjust state when a prop changes" pattern, during render rather than in an effect:
  // an effect would paint one frame still holding the previous budget's countdown.
  if (budget !== seconds) {
    setBudget(seconds);
    setRemaining(seconds);
    setCancelled(false);
  }

  const running = !cancelled && remaining !== null && remaining > 0;

  // The last tick fires `onElapsed` instead of scheduling another one, so the whole countdown lives in a
  // single cancellable timer. That is what keeps the action from running twice: StrictMode's run → clean up
  // → run again leaves exactly one timer scheduled, with no guard flag to reset.
  useEffect(() => {
    if (!running || remaining === null) return;
    const timer = window.setTimeout(() => {
      setRemaining(remaining - 1);
      if (remaining <= 1) onElapsedRef.current();
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [running, remaining]);

  const cancel = useCallback(() => setCancelled(true), []);

  return { remaining: running ? remaining : null, cancel };
}
